import { findCmpaignAUdienceBoardsColumns } from '@/common/reducers/CampaignThunks';
import { getTemplatePlaceholderListForCampaign } from '@common-reducers/CampaignSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { CONTACT_TOKEN_FIELDS, normalizeTokenKey } from '@bt-common/schemas/bt/personalization-tokens';
import { theme } from '@common/theme';
import { ArrowDropDown } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useEffect, useMemo, useState } from 'react';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { MessageEditorMessageBoxRef } from './message-editor-message-box';

type Props = {
    messageBoxRef?: React.RefObject<MessageEditorMessageBoxRef>;
    // When provided, clicking a placeholder calls this instead of inserting into the
    // Lexical message box — lets the same bar drive other fields (e.g. poll question/options).
    onInsert?: (placeholder: string) => void;
}

export default function TemplatePlaceholderBar({
    messageBoxRef,
    onInsert,
}: Props) {
    const dispatch = useAppDispatch()
    const templatePlaceholderList: string[] = useAppSelector((state) => getTemplatePlaceholderListForCampaign(state))

    const [placeholderDropdownAnchorEl, setPlaceholderDropdownAnchorEl] = useState<HTMLElement | null>(null)

    // Shadow-DOM portal target. In the EXTENSION this bar renders inside the
    // injected shadow root, and an MUI Menu portals to `document.body` by
    // default — i.e. outside the shadow tree, where neither the injected
    // stylesheet nor the shell's stacking context reaches, so the menu was
    // effectively invisible/unclickable there while working fine on the website.
    // Same treatment the emoji Menu in `message-editor-controlled` already gets.
    const portalContainer = usePortalContainer();
    const menuContainer = useMemo(() => {
        // MUI takes an HTMLElement, never a ShadowRoot.
        const el = portalContainer instanceof HTMLElement ? portalContainer : null;
        // Outside a shadow DOM (the website) leave it undefined → MUI's own default.
        return el && el !== document.body ? el : undefined;
    }, [portalContainer]);

    // Offered tokens = the canonical contact fields (SSoT — always resolvable) plus
    // any extra columns on the selected audience, deduped (case/space-insensitive).
    // Replaces the old hardcoded First/Last/Phone (template mode) vs board-columns
    // (campaign mode) split, so every surface offers the same correct tokens — and
    // they always include the canonical set even before audience columns load.
    const offered = React.useMemo<string[]>(() => {
        const canonical = CONTACT_TOKEN_FIELDS.map((f) => f.label);
        const seen = new Set(canonical.map(normalizeTokenKey));
        const dynamic: string[] = [];
        for (const t of templatePlaceholderList) {
            const n = normalizeTokenKey(t);
            if (!seen.has(n)) { seen.add(n); dynamic.push(t); }
        }
        return [...canonical, ...dynamic];
    }, [templatePlaceholderList]);




    const handlePlaceholderDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
        setPlaceholderDropdownAnchorEl(event.currentTarget);
    };

    const handlePlaceholderDropdownClose = () => {
        setPlaceholderDropdownAnchorEl(null);
    };

    const handleClickPlaceholder = (placeholder: string) => {
        if (onInsert) {
            onInsert(placeholder);
            return;
        }
        if (messageBoxRef?.current) {
            messageBoxRef.current.insertTextAtCursor(`{${placeholder}}`);
        }
    };



    useEffect(() => {
       dispatch(findCmpaignAUdienceBoardsColumns())
    }, [])

    return (
        <Stack direction="row" alignItems="center"
            style={{
                marginBottom: theme.spacing(1),
                marginTop: theme.spacing(1),
                gap: '10px',
                padding: '5px',
                // Personalization chips wrap to a second row on narrow screens instead of
                // pushing the last chip past the edge. No effect where the row already fits.
                flexWrap: 'wrap',
            }}>
            {offered.slice(0, 4).map((val, index) =>
                <Button key={index}
                    sx={{
                        border: '1px solid rgba(0,0,0,0.05)',
                        borderRadius: '16px',
                        height: '27px',
                        width: 'auto',
                        textTransform: 'none',
                        color: 'var(--color-primary)',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleClickPlaceholder(val)}
                >
                    {val}
                </Button>)}
            {offered.length > 4 &&
                <div>
                    {/* The overflow trigger. A bare ▾ icon named nothing and looked
                        like a stray glyph next to the chips — it now reads as one
                        more chip ("+N more"), matching what it opens. */}
                    <Button
                        aria-controls={placeholderDropdownAnchorEl ? 'placeholder-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={Boolean(placeholderDropdownAnchorEl)}
                        title="More personalization fields"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handlePlaceholderDropdownClick}
                        endIcon={<ArrowDropDown />}
                        sx={{
                            border: '1px solid rgba(0,0,0,0.05)',
                            borderRadius: '16px',
                            height: '27px',
                            width: 'auto',
                            textTransform: 'none',
                            color: 'var(--color-primary)',
                            '& .MuiButton-endIcon': { marginLeft: '2px' },
                        }}
                    >
                        +{offered.length - 4} more
                    </Button>
                    <Menu
                        id="placeholder-menu"
                        anchorEl={placeholderDropdownAnchorEl}
                        keepMounted
                        open={Boolean(placeholderDropdownAnchorEl)}
                        onClose={handlePlaceholderDropdownClose}
                        {...(menuContainer ? { container: menuContainer } : {})}
                        // Keep focus in the Lexical editor so its selection survives
                        // the menu — the insert then lands at the caret rather than
                        // at the end of the message.
                        disableAutoFocus
                        disableEnforceFocus
                        sx={{
                            // Above the extension shell's own stacking context, matching
                            // the emoji Menu next door.
                            zIndex: 21000,
                            pointerEvents: 'auto',
                            '& .MuiPaper-root': { zIndex: 21000, pointerEvents: 'auto' },
                            '& .MuiList-root': { pointerEvents: 'auto' },
                        }}
                    >
                        {/* The MenuItem itself inserts and closes. It used to wrap a
                            nested <Button>, so the insert depended on the click
                            bubbling from the button to the item — clicking the
                            padding around the button only closed the menu. */}
                        {offered.slice(4).map((val, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    handleClickPlaceholder(val);
                                    handlePlaceholderDropdownClose();
                                }}
                                sx={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}
                            >
                                {val}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
            }
        </Stack >
    )
}